// Work-order lists: keep tab / page / per-page / search / sort in the URL query (like Global Search),
// so coming back from a detail page reopens the list as it was. No query (side menu) → defaults.
// The view needs: page, limit, search, selectedSort, tableHeaders, setSort(), fetchList();
// with tabs also selectedTab and a `listTabs: { queryName: tabValue }` component option.
import { filtersFromQuery, filterParams } from "./listFilters";
import { saveListScroll, takeListScroll, cameBack, restoreListScroll } from "./scrollRestore";

const LIMITS = [25, 50, 100];
const DEFAULT_LIMIT = 25;
const LIST_KEYS = ['tab', 'page', 'limit', 'search', 'sort', 'dir'];

// stable string of a query object, to tell our own replace() apart from outside navigation
function queryKey(query) {
    return JSON.stringify(Object.keys(query).sort().map(k => [k, query[k]]));
}

export default {
    data() {
        // filters from the header filter button (ListFilterButton), kept in the URL as f.<param>=…
        return { listFilters: {} };
    },
    computed: {
        // spread into each list call
        listFilterParams() {
            return filterParams(this.listFilters);
        },
    },
    created() {
        this.listRouteName = this.$route.name;
        this.listDefaults = {
            sort: { ...this.selectedSort },
            tab: this.$options.listTabs ? this.selectedTab : undefined,
        };
        this.listQueryKey = null;
        // back from a detail page → scroll to where the user was once the rows are in (see `loading` watcher)
        this.pendingListScroll = cameBack() ? takeListScroll(this.$route.fullPath) : null;
    },
    // remember the scroll position of this exact list URL (page / filters / sort included)
    beforeRouteLeave(to, from, next) {
        saveListScroll(from.fullPath);
        next();
    },
    beforeUnmount() {
        // a pending search must not fire (and rewrite the URL) after leaving the list
        clearTimeout(this.debounceTimer);
    },
    watch: {
        loading(isLoading) {
            if (isLoading || this.pendingListScroll == null) return
            const y = this.pendingListScroll;
            this.pendingListScroll = null;
            this.$nextTick(() => restoreListScroll(y));
        },
        // same list, different query from outside (e.g. side menu link while on the list) → reopen from the URL
        '$route.query'(query) {
            if (this.$route.name !== this.listRouteName) return
            if (queryKey(query) === this.listQueryKey) return
            clearTimeout(this.debounceTimer);
            this.restoreListState();
            this.fetchList();
        },
    },
    methods: {
        // read the list state from the URL; missing / invalid values fall back to the defaults
        restoreListState() {
            const q = this.$route.query;
            const page = parseInt(q.page, 10);
            this.page = page > 0 && String(page) === String(q.page) ? page : 1;
            const limit = Number(q.limit);
            this.limit = LIMITS.includes(limit) ? limit : DEFAULT_LIMIT;
            this.search = typeof q.search === 'string' ? q.search : '';

            const sortable = this.tableHeaders.filter(h => h.sort_applicable == '1').map(h => h.sort_column);
            this.selectedSort = sortable.includes(q.sort) && ['ASC', 'DESC'].includes(q.dir)
                ? { column: q.sort, direction: q.dir }
                : { ...this.listDefaults.sort };
            this.setSort();

            this.listFilters = filtersFromQuery(q);

            const tabs = this.$options.listTabs;
            if (tabs) {
                this.selectedTab = Object.prototype.hasOwnProperty.call(tabs, q.tab) ? tabs[q.tab] : this.listDefaults.tab;
            }
        },
        // write the list state to the URL (replace: paging / sorting don't add history entries); other keys are kept
        saveListState() {
            if (this.$route.name !== this.listRouteName) return
            const query = {};
            Object.keys(this.$route.query).forEach(k => {
                if (!LIST_KEYS.includes(k)) query[k] = this.$route.query[k];
            });
            const tabs = this.$options.listTabs;
            if (tabs && this.selectedTab !== this.listDefaults.tab) {
                const name = Object.keys(tabs).find(k => tabs[k] === this.selectedTab);
                if (name) query.tab = name;
            }
            if (this.page > 1) query.page = String(this.page);
            if (this.limit != DEFAULT_LIMIT) query.limit = String(this.limit);
            if (this.search && this.search.trim()) query.search = this.search;
            const def = this.listDefaults.sort;
            if (this.selectedSort.column !== def.column || this.selectedSort.direction !== def.direction) {
                query.sort = this.selectedSort.column;
                query.dir = this.selectedSort.direction;
            }
            this.listQueryKey = queryKey(query);
            if (queryKey(this.$route.query) === this.listQueryKey) return
            this.$router.replace({ path: this.$route.path, query }).catch(() => { });
        },
        // a restored page past the end (records went away meanwhile) → load the last page
        fixListPage() {
            if (this.page > 1 && this.total_pages > 0 && this.page > this.total_pages) {
                this.page = this.total_pages;
                this.fetchList();
            }
        },
    },
};
